import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Phone, Mail, ArrowLeft, CheckCircle2, Calculator } from 'lucide-react';
import { Section } from '../components/ui/Common.jsx';
import { companyInfo, services } from '../data.js';
import ResponsiveImage from '../components/ui/ResponsiveImage.jsx';
import TaxaCalculator from '../components/TaxaCalculator.jsx';

export default function Taxa() {
  const serviceId = "taxa";
  const otherServices = services.filter((s) => s.id !== serviceId);

  return (
    <>
      <Helmet>
        <title>Taxa i Thy og på Mors | {companyInfo.name}</title>
        <meta name="description" content="Bestil nemt og hurtigt en taxa i Thy og på Mors. Vi står til rådighed døgnet rundt med professionelle chauffører." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[400px] w-full overflow-hidden">
        <div className="absolute inset-0">
          <ResponsiveImage
            src="https://assets.auxodev.dk/assets/80af3c4d-4953-436f-a9f5-4abfeafc9910/98dd9867-410a-47f4-b860-f333f9cd9dba.jpg"
            alt="Taxa i Thy og Mors"
            className="h-full w-full object-cover"
            sizes="100vw"
          />
          <div className="absolute inset-0 bg-slate-900/60" />
        </div>
        <div className="container relative z-10 mx-auto flex h-full items-end pb-20 px-4 md:px-6">
          <div className="max-w-3xl">
            <Link
              to="/"
              className="mb-8 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-blue-400 hover:text-blue-300 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" /> Tilbage til forsiden
            </Link>
            <h1 className="text-5xl font-bold text-white md:text-6xl">Taxa i Thy og på Mors</h1>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <Section>
        <div className="grid gap-16 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-12">
            
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-slate-900">Taxa kørsel 24/7 – altid klar til at hjælpe dig</h2>
              <p className="text-xl text-slate-600 leading-relaxed">
                Bestil nemt og hurtigt en taxa. Vi er dedikerede til at levere den bedste service til vores kunder, uanset om det drejer sig om en kort tur rundt i byen eller en længere køretur til for eksempel lufthavnen.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                Vi forstår, at behovet for transport kan opstå på alle tidspunkter af døgnet, og derfor står vores taxaer til rådighed døgnet rundt. Uanset om det er tidligt om morgenen, sent om aftenen eller midt om natten, kan du regne med sikker transport til tiden.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <ResponsiveImage 
                src="https://assets.auxodev.dk/assets/80af3c4d-4953-436f-a9f5-4abfeafc9910/3098352a-1dff-4675-8613-9bff44b1bea5.jpg" 
                alt="Taxa service 1" 
                className="h-64 w-full object-cover object-[center_70%] shadow-md"
                sizes="(max-width: 768px) 50vw, 400px"
              />
              <ResponsiveImage 
                src="https://assets.auxodev.dk/assets/80af3c4d-4953-436f-a9f5-4abfeafc9910/98dd9867-410a-47f4-b860-f333f9cd9dba.jpg" 
                alt="Taxa service 2" 
                className="h-64 w-full object-cover object-[center_70%] shadow-md"
                sizes="(max-width: 768px) 50vw, 400px"
              />
            </div>

            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-slate-900">Professionelle chauffører</h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                Vores taxachauffører er professionelle og imødekommende. De har indgående kendskab til området og går gerne langt for at sikre, den bedst mulige service, så du kan læne dig tilbage og nyde turen.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <ResponsiveImage 
                src="https://assets.auxodev.dk/assets/80af3c4d-4953-436f-a9f5-4abfeafc9910/d7fd8e1e-f9dc-41ce-a549-b8f870fe9868.jpg" 
                alt="Taxa service 3" 
                className="h-64 w-full object-cover object-[center_70%] shadow-md"
                sizes="(max-width: 768px) 50vw, 400px"
              />
              <ResponsiveImage 
                src="https://assets.auxodev.dk/assets/80af3c4d-4953-436f-a9f5-4abfeafc9910/8b21f758-2a69-4e9f-be84-c3b4018d18cc.jpg" 
                alt="Taxa service 4" 
                className="h-64 w-full object-cover shadow-md"
                sizes="(max-width: 768px) 50vw, 400px"
              />
            </div>

            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-slate-900">Priser på taxa kørsel</h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                Vi vil gerne være din foretrukne taxi i Thy og på Mors. Uanset om du har brug for en taxa til lufthavnen, en gruppeudflugt eller bare en hurtig tur rundt i byen, gør Sydthy Taxi & Turistfart ApS det nemt og bekvemt.
              </p>
              
              <div className="bg-slate-50 p-8 border border-slate-100 space-y-4">
                <h3 className="text-xl font-bold text-slate-900">Særlige tillæg:</h3>
                <ul className="space-y-3">
                  {[
                    "Transport af cykel (kun ved forudbestilling): 30 kr.",
                    "Transport af kørestolsbrugere: 100 kr.",
                    "Rengøring efter opkast: 1.500 kr."
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-slate-600">
                      <CheckCircle2 className="h-5 w-5 text-blue-600 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="pt-4">
                  <Link 
                    to="/taxa/taxa-priser"
                    className="inline-flex items-center gap-2 bg-blue-600 px-6 py-3 font-semibold text-white transition-colors hover:bg-blue-700"
                  >
                    Se alle takster her
                  </Link>
                </div>
              </div>
            </div>

            <div>
              <ResponsiveImage 
                src="https://assets.auxodev.dk/assets/80af3c4d-4953-436f-a9f5-4abfeafc9910/1afea2ba-7c24-4dc4-be32-bc44fd48512e.jpg" 
                alt="Taxa service fuld bredde" 
                className="h-96 w-full object-cover shadow-md"
                sizes="(max-width: 1024px) 100vw, 1200px"
              />
            </div>

          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-32 space-y-8">
              <div className="bg-slate-900 p-8 text-white">
                <h3 className="mb-6 text-2xl font-bold">Bestil Taxa</h3>
                <p className="mb-8 text-slate-400">
                  Vi kører døgnet rundt i hele Thy og på Mors. Ring direkte til vognen for hurtig afhentning.
                </p>
                <div className="space-y-4">
                  <a
                    href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                    className="flex w-full items-center justify-center gap-3 bg-blue-600 py-4 font-bold transition-colors hover:bg-blue-700"
                  >
                    <Phone className="h-5 w-5" />
                    Ring {companyInfo.phone}
                  </a>
                  <a
                    href={`mailto:${companyInfo.email}`}
                    className="flex w-full items-center justify-center gap-3 border border-white/20 py-4 font-bold transition-colors hover:bg-white/10"
                  >
                    <Mail className="h-5 w-5" />
                    Send en mail
                  </a>
                  <Link
                    to="/taxa/taxa-priser#beregn-pris"
                    className="flex w-full items-center justify-center gap-3 py-4 font-bold text-white transition-opacity hover:opacity-90"
                    style={{ backgroundColor: '#2CAAD4' }}
                  >
                    <Calculator className="h-5 w-5" />
                    Beregn din pris
                  </Link>
                </div>
              </div>

              <div className="bg-slate-50 p-8 border border-slate-200">
                <h4 className="mb-4 font-bold text-slate-900 uppercase tracking-wider text-sm">Andre ydelser</h4>
                <ul className="space-y-3">
                  {otherServices.map((s) => (
                    <li key={s.id}>
                      <Link
                        to={s.id === 'taxa' ? '/taxa' : `/ydelser/${s.id}`}
                        className="flex items-center justify-between text-slate-600 hover:text-blue-600 transition-colors group"
                      >
                        {s.title}
                        <ArrowLeft className="h-4 w-4 rotate-180 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Prisberegner i fuld bredde */}
      <Section className="bg-slate-50 border-t border-slate-100" padding="compact">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900">Beregn din pris</h2>
            <p className="text-slate-600 mt-2">Brug vores interaktive prisberegner til at estimere prisen på din næste tur.</p>
          </div>
          <TaxaCalculator />
        </div>
      </Section>
    </>
  );
}
